import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { EngineHealthWidget } from '../../components/admin/EngineHealthWidget';
import { AuditModal } from '../../components/admin/AuditModal';
import { MaintenanceToggle } from '../../components/admin/MaintenanceToggle';
import { 
  GitBranch, 
  Radio, 
  BarChart3,
  Wrench,
  FileText,
  Users,
  TrendingUp,
  ArrowRight,
  Loader2,
  AlertCircle
} from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  var navigate = useNavigate();
  var auth = useAuth();
  var [stats, setStats] = useState(null);
  var [statsLoading, setStatsLoading] = useState(true);
  var [auditOpen, setAuditOpen] = useState(false);

  useEffect(function() {
    if (!auth.loading && !auth.isAdmin) {
      navigate('/login', { state: { from: { pathname: '/admin' } } });
    }
  }, [auth.isAdmin, auth.loading, navigate]);

  useEffect(function() {
    if (auth.isAdmin && auth.token) {
      fetchStats();
    }
  }, [auth.isAdmin, auth.token]);

  function fetchStats() {
    fetch(API_URL + '/api/admin/dashboard-stats', {
      headers: { 'Authorization': 'Bearer ' + auth.token }
    })
      .then(function(r) { return r.json(); })
      .then(function(d) { setStats(d); setStatsLoading(false); })
      .catch(function() { setStatsLoading(false); });
  }

  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  var pendingReview = stats ? stats.pending_review : 0;
  var publishedToday = stats ? stats.published_today : 0;
  var activeContributors = stats ? stats.active_contributors : 0;
  var outputChannels = stats ? stats.output_channels : 5;
  var totalStories = stats ? stats.total_stories : 0;
  var pendingUsers = stats ? stats.pending_users : 0;

  var quickActions = [
    {
      title: 'Story Journey',
      description: 'Review and guide incoming stories',
      icon: GitBranch,
      path: '/admin/pipeline',
      color: 'bg-blue-500',
    },
    {
      title: 'Where Stories Go',
      description: 'Channels amplifying voices',
      icon: Radio,
      path: '/admin/outputs',
      color: 'bg-orange-500',
    },
    {
      title: 'Impact',
      description: 'What is changing',
      icon: BarChart3,
      path: '/admin/evidence',
      color: 'bg-green-500',
    },
    {
      title: 'Country Toolkits',
      description: 'Resources for country teams',
      icon: Wrench,
      path: '/admin/toolkits',
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Welcome to Grow Your Tomorrow</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Platform Access — Maintenance Mode toggle */}
        <div className="mb-6">
          <MaintenanceToggle token={auth.token} />
        </div>

        {/* Engine Health */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-1">
            <EngineHealthWidget token={auth.token} onOpenAudit={function() { setAuditOpen(true); }} />
          </div>
          <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
        {/* Quick Stats */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={function() { navigate('/admin/pipeline'); }}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900" data-testid="stat-pending-review">
                  {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : pendingReview}
                </p>
                <p className="text-sm text-gray-600">Pending Review</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900" data-testid="stat-published-today">
                  {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : publishedToday}
                </p>
                <p className="text-sm text-gray-600">Published Today</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={function() { navigate('/admin/users'); }}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900" data-testid="stat-contributors">
                  {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : activeContributors}
                </p>
                <p className="text-sm text-gray-600">Active Contributors</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={function() { navigate('/admin/outputs'); }}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Radio className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900" data-testid="stat-output-channels">
                  {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : outputChannels}
                </p>
                <p className="text-sm text-gray-600">Channels</p>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>

        <AuditModal open={auditOpen} onClose={function() { setAuditOpen(false); }} token={auth.token} />

        {/* Alert for pending items */}
        {pendingUsers > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-amber-100 transition-colors" onClick={function() { navigate('/admin/users'); }} data-testid="pending-users-alert">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{pendingUsers} contributor{pendingUsers > 1 ? 's' : ''}</strong> pending approval.
            </p>
            <ArrowRight className="w-4 h-4 text-amber-600 ml-auto" />
          </div>
        )}

        {/* Quick Actions */}
        <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map(function(action) {
            var IconComp = action.icon;
            return (
              <Card key={action.path} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={function() { navigate(action.path); }} data-testid={'action-' + action.title.toLowerCase().replace(/ /g, '-')}>
                <CardContent className="p-6">
                  <div className={'w-12 h-12 rounded-xl flex items-center justify-center mb-4 ' + action.color}>
                    <IconComp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{action.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{action.description}</p>
                  <Button variant="ghost" className="text-[#07743F] hover:text-[#065a32] p-0 h-auto">
                    Open <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* System Flow */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">The Story Journey</h2>
              <p className="text-sm text-gray-500">{totalStories} total stories in system</p>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-blue-600">1</span>
                </div>
                <span>Story Capture</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-yellow-600">2</span>
                </div>
                <span>Curation & Gates</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-green-600">3</span>
                </div>
                <span>Stories</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-orange-600">4</span>
                </div>
                <span>Amplify</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-purple-600">5</span>
                </div>
                <span>Impact</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
